import React, { useMemo, useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SendIcon, AlertCircleIcon } from 'lucide-react';
import { FormSection, TextField, TextAreaField, DisplayField } from '../components/FormComponents';
import { mockRequisitions, mockStockEntries, mockProducts } from '../data/mockData';
export function StockIssueForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const requisition = mockRequisitions.find(r => r.id === id);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  // Initialize issue quantities for each item
  const [issueQuantities, setIssueQuantities] = useState<Record<string, string>>(requisition?.items.reduce((acc, item) => ({
    ...acc,
    [item.id]: item.qty.toString()
  }), {}) || {});
  // Compute available stock for each product
  const stockBalances = useMemo(() => {
    const balances = new Map<string, number>();
    mockStockEntries.forEach(entry => {
      if (entry.status !== 'Completed') return;
      // We'll aggregate by product name since requisition items use product names
      const product = mockProducts.find(p => p.productCode === entry.productCode);
      if (!product) return;
      const current = balances.get(product.productName) || 0;
      const amount = entry.type === 'Stock In' ? entry.quantity : -entry.quantity;
      balances.set(product.productName, current + amount);
    });
    return balances;
  }, []);
  if (!requisition) {
    return <div className="p-8 text-center" data-id="element-2404">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-2405">
          Requisition not found
        </h2>
        <button onClick={() => navigate('/issue')} className="mt-4 text-indigo-600 hover:text-indigo-800" data-id="element-2406">
          Back to Issue List
        </button>
      </div>;
  }
  const handleQuantityChange = (itemId: string, value: string) => {
    setIssueQuantities(prev => ({
      ...prev,
      [itemId]: value
    }));
  };
  const handleSubmit = () => {
    // Validation
    let hasErrors = false;
    let errorMessages: string[] = [];
    requisition.items.forEach(item => {
      const issueQty = Number(issueQuantities[item.id]);
      const availableQty = stockBalances.get(item.item) || 0;
      if (isNaN(issueQty) || issueQty < 0) {
        hasErrors = true;
        errorMessages.push(`Invalid quantity for ${item.item}`);
      } else if (issueQty > availableQty) {
        hasErrors = true;
        errorMessages.push(`Cannot issue ${issueQty} of ${item.item}. Only ${availableQty} available in stock.`);
      }
    });
    if (hasErrors) {
      alert('Validation Errors:\n\n' + errorMessages.join('\n'));
      return;
    }
    alert('Stock issued successfully!');
    navigate('/issue');
  };
  return <div className="space-y-6 max-w-5xl mx-auto" data-id="element-2407">
      <div className="flex items-center gap-3" data-id="element-2408">
        <div className="p-3 bg-amber-100 rounded-lg" data-id="element-2409">
          <SendIcon className="h-6 w-6 text-amber-600" data-id="element-2410" />
        </div>
        <div data-id="element-2411">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2412">Issue Stock</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2413">
            Issue inventory against Requisition {requisition.reqNo}
          </p>
        </div>
      </div>

      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-2414">
        <FormSection title="Requisition Details" data-id="element-2415">
          <DisplayField label="REQ No" value={requisition.reqNo} data-id="element-2416" />
          <DisplayField label="Department" value={requisition.department} data-id="element-2417" />
          <DisplayField label="Requested By" value={requisition.requestedBy} data-id="element-2418" />
          <DisplayField label="Required Date" value={requisition.requiredDate} data-id="element-2419" />
          <DisplayField label="Usage Type" value={requisition.reqFor} data-id="element-2420" />
          {requisition.employee && <DisplayField label="Employee" value={requisition.employee} data-id="element-2421" />}
        </FormSection>

        <FormSection title="Issue Details" data-id="element-2422">
          <TextField label="Issue Date" type="date" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} required data-id="element-2423" />
        </FormSection>

        <div className="mb-8" data-id="element-2424">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-2425">
            Items to Issue
          </h3>

          <div className="overflow-x-auto" data-id="element-2426">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-2427">
              <thead className="bg-gray-50" data-id="element-2428">
                <tr data-id="element-2429">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2430">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2431">
                    Purpose
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-2432">
                    Req. Qty
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-2433">
                    Available Stock
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32" data-id="element-2434">
                    Issue Qty
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2435">
                {requisition.items.map(item => {
                const availableQty = stockBalances.get(item.item) || 0;
                const issueQty = Number(issueQuantities[item.id]) || 0;
                const isOverStock = issueQty > availableQty;
                return <tr key={item.id} className={isOverStock ? 'bg-red-50' : ''} data-id="element-2436">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2437">
                        {item.item}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2438">
                        {item.purpose}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900" data-id="element-2439">
                        {item.qty}
                      </td>
                      <td className="px-4 py-3 text-sm text-center" data-id="element-2440">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${availableQty > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2441">
                          {availableQty}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-2442">
                        <input type="number" min="0" max={availableQty} className={`block w-full rounded-md shadow-sm sm:text-sm border p-1.5 text-center ${isOverStock ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-amber-500 focus:border-amber-500'}`} value={issueQuantities[item.id]} onChange={e => handleQuantityChange(item.id, e.target.value)} data-id="element-2443" />
                        {isOverStock && <p className="mt-1 text-xs text-red-600 flex items-center justify-center" data-id="element-2444">
                            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-2445" /> Exceeds
                            stock
                          </p>}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>

        <FormSection title="Additional Information" data-id="element-2446">
          <div className="md:col-span-2" data-id="element-2447">
            <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={3} placeholder="Reason for issuing stock, condition notes, etc..." data-id="element-2448" />
          </div>
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-2449">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2450">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700" data-id="element-2451">
            Issue Stock
          </button>
        </div>
      </form>
    </div>;
}